import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ReturnAlertPopup } from '@/components/warehouse/ReturnAlertPopup';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Sun, Moon, Monitor, LogOut, Menu, Eye, Search, X, Waves, Briefcase, Leaf,
  Link2, Shield, ChevronLeft, ChevronRight, Command,
} from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import PharmacyModeBar from '@/components/PharmacyModeBar';
import { getMessagesUnreadCount } from '@/lib/api';
import { usePharmacyMode } from '@/contexts/PharmacyModeContext';
import { CommandPalette } from '@/components/CommandPalette';

// Sidebar sub-components
import {
  PLATFORM_SECTIONS, ADMIN_SECTIONS, MANAGER_SECTIONS, MR_SECTIONS, APTEKA_SECTIONS,
  SB, ROLE_DISPLAY, PREVIEW_DISPLAY, PREVIEW_ROLES,
} from '@/components/sidebar/sidebarConfig';
import { SidebarNavItem } from '@/components/sidebar/SidebarNavItem';
import { ThemePicker } from '@/components/sidebar/ThemePicker';
import { LanguagePicker } from '@/components/sidebar/LanguagePicker';
import { HelpButton } from '@/components/sidebar/HelpButton';
import { GlobalSearch } from '@/components/sidebar/GlobalSearch';

// ============ MAIN LAYOUT ============
export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const { user, logout, previewRole, setPreviewRole, realUser, isPlatformUser, workspaceName, workspaceType } = useAuth();
  const { hasAccess } = usePermissions();
  const { language, setLanguage, languageNames, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const pharmacyMode = usePharmacyMode();

  const canPreview = !isPlatformUser && (realUser?.role === 'company_owner' || realUser?.role === 'company_admin');
  const role = user?.role || 'pharmacy_owner';
  const isApteka = ['apteka', 'pharmacy_owner', 'pharmacy_admin'].includes(role);
  const isMr = role === 'mr';

  // Role-based section selection
  const isPlatform = ['platform_owner', 'platform_admin', 'support_admin'].includes(role);
  const sections = (() => {
    if (isPlatform && !previewRole) return PLATFORM_SECTIONS;
    if (isApteka) return APTEKA_SECTIONS;
    if (isMr) return MR_SECTIONS;
    if (role === 'manager') return MANAGER_SECTIONS;
    return ADMIN_SECTIONS;
  })();

  const currentPreviewIdx = previewRole ? PREVIEW_ROLES.indexOf(previewRole) : -1;

  const togglePreview = () => {
    if (previewRole) { setPreviewRole(null); navigate('/dashboard'); }
    else { setPreviewRole('manager'); navigate('/dashboard'); }
  };
  const cyclePreviewRole = () => {
    const nextIdx = (currentPreviewIdx + 1) % PREVIEW_ROLES.length;
    setPreviewRole(PREVIEW_ROLES[nextIdx]);
    navigate('/dashboard');
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Unread messages count
  const [msgUnread, setMsgUnread] = useState(0);
  const fetchMsgUnread = useCallback(async () => {
    try { const { data } = await getMessagesUnreadCount(); setMsgUnread(data.count || 0); } catch { /* ignore */ }
  }, []);
  useEffect(() => { fetchMsgUnread(); const i = setInterval(fetchMsgUnread, 30000); return () => clearInterval(i); }, [fetchMsgUnread]);

  // Low stock badge count
  const [lowStockCount, setLowStockCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { getLowStockCount } = await import('@/lib/api');
        const { data } = await getLowStockCount();
        if (!cancelled) setLowStockCount(data.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const i = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(i); };
  }, []);

  // Autopilot pending badge count
  const [autopilotCount, setAutopilotCount] = useState(0);
  const [ordersPendingCount, setOrdersPendingCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { getOrdersPendingCount } = await import('@/lib/api');
        const { data } = await getOrdersPendingCount();
        if (!cancelled) setOrdersPendingCount(data.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const i = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(i); };
  }, []);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { getAutopilotCount } = await import('@/lib/api');
        const { data } = await getAutopilotCount();
        if (!cancelled) setAutopilotCount(data.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const i = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(i); };
  }, []);

  // Ctrl+K for command palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const themeIcon = theme === 'dark' ? Moon : theme === 'corporate' ? Briefcase : theme === 'mint' ? Leaf : theme === 'ocean' ? Waves : theme === 'light' ? Sun : Monitor;
  const ThemeIcon = themeIcon;

  const location = useLocation();
  const activePageLabel = (() => {
    for (const section of sections) {
      for (const item of section.items) {
        if (item.path === location.pathname) return t(item.labelKey);
        if (item.children) {
          const child = item.children.find(c => c.path === location.pathname);
          if (child) return t(child.labelKey);
        }
      }
    }
    return t('dashboard');
  })();

  // Rule 4: Auto-open parent when a child route is active (handles refresh + command palette navigation)
  useEffect(() => {
    const fullPath = location.pathname + location.hash;
    for (const section of sections) {
      for (const item of section.items) {
        if (item.children) {
          const childActive = item.children.some(c => c.path === fullPath || c.path === location.pathname);
          if (childActive) {
            setOpenMenuId(item.path);
            return;
          }
        }
      }
    }
  }, [location.pathname, location.hash, sections]);

  // Auto-close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Brand Header with Logo */}
      <div className={`flex items-center gap-3 h-14 border-b border-border shrink-0 transition-all duration-300 ${collapsed ? 'justify-center px-2' : 'px-4'}`}>
        <div className="relative shrink-0">
          <img
            src="/logo-final-e.svg"
            alt="Pharm-X"
            className={`${collapsed ? 'h-6' : 'h-7'} w-auto object-contain`}
            data-testid="sidebar-logo"
          />
        </div>
        {!collapsed && (
          <>
            <div className="min-w-0 overflow-hidden transition-opacity duration-200 flex-1">
              <span className="font-bold text-[14px] tracking-tight block truncate">{workspaceName || 'PharmX'}</span>
              {workspaceType && (
                <span className="text-[10px] text-muted-foreground capitalize">{workspaceType === 'company' || workspaceType === 'pharma_company' ? 'Pharma Company' : workspaceType === 'pharmacy' ? 'Pharmacy' : workspaceType}</span>
              )}
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden w-8 h-8 rounded-md flex items-center justify-center hover:bg-primary/5 transition-colors"
              data-testid="mobile-sidebar-close"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Pharmacy Mode Indicator */}
      {pharmacyMode.isPharmacy && !collapsed && (
        <div className="px-3 py-2 border-b border-border shrink-0" data-testid="pharmacy-mode-badge">
          {pharmacyMode.isConnected ? (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/20">
              <Link2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <div className="min-w-0">
                <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 block">Connected Mode</span>
                <span className="text-[9px] text-emerald-600/80 dark:text-emerald-400/80 block truncate">{pharmacyMode.primaryCompany}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/20">
              <Shield className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
              <div className="min-w-0">
                <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-300 block">Manual Mode</span>
                <span className="text-[9px] text-amber-600/80 dark:text-amber-400/80 block">Full access</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Command Palette Button */}
      {!collapsed ? (
        <div className="px-3 pt-3 pb-0 shrink-0">
          <button onClick={() => setCmdOpen(true)}
            className="w-full flex items-center gap-2 px-2.5 py-2 bg-muted/50 border border-border rounded-xl text-[13px] text-muted-foreground hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 cursor-text"
            data-testid="sidebar-cmd-trigger">
            <Search className="w-3.5 h-3.5" />
            <span className="flex-1 text-left">Search or command...</span>
            <kbd className="text-[10.5px] px-1.5 py-0.5 rounded-md bg-primary/10 text-muted-foreground font-mono">&#8984;K</kbd>
          </button>
        </div>
      ) : (
        <div className="px-1.5 pt-2 shrink-0">
          <button onClick={() => setCmdOpen(true)}
            className="w-full flex items-center justify-center py-2.5 rounded-xl text-muted-foreground hover:bg-primary/5 hover:text-primary transition-colors"
            title="Command Palette ⌘K" data-testid="sidebar-cmd-trigger-collapsed">
            <Search className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Section Label */}
      {!collapsed && (
        <div className="px-4 pt-3 pb-1 shrink-0">
          <span className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-muted-foreground/50">NAVIGATION</span>
        </div>
      )}

      {/* Grouped Nav */}
      <nav className="flex-1 overflow-y-auto py-1 px-2" data-testid="sidebar-nav">
        {sections.map(section => {
          const visibleItems = section.items.filter(item => item.permKey === null || hasAccess(item.permKey));
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.key} className="mb-1">
              {!collapsed && section.label && (
                <div className="px-2.5 pt-3 pb-1">
                  <span className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] text-muted-foreground/50" data-testid={`section-${section.key}`}>
                    {section.label}
                  </span>
                </div>
              )}
              {visibleItems.map(item => (
                <SidebarNavItem
                  key={item.path}
                  item={item}
                  collapsed={collapsed}
                  t={t}
                  hasAccess={hasAccess}
                  setMobileOpen={setMobileOpen}
                  msgUnread={msgUnread}
                  lowStockCount={lowStockCount}
                  autopilotCount={autopilotCount}
                  ordersPendingCount={ordersPendingCount}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                  location={location}
                />
              ))}
            </div>
          );
        })}

        {/* Theme & Language — inside scrollable nav so they never clip */}
        {!collapsed && (
          <div style={{ borderTop: `1px solid ${SB.border}`, marginTop: 8, paddingTop: 8 }}>
            <div style={{ padding: '0 2px', marginBottom: 2 }}>
              <span style={{ fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: SB.mutedSoft }}>PREFERENCES</span>
            </div>
          </div>
        )}
        <ThemePicker theme={theme} setTheme={setTheme} collapsed={collapsed} ThemeIcon={ThemeIcon} />
        <LanguagePicker language={language} setLanguage={setLanguage} languageNames={languageNames} collapsed={collapsed} />

        {/* Help & Status — inside scrollable area to reduce footer height on small screens */}
        {!isPlatform && <HelpButton collapsed={collapsed} setMobileOpen={setMobileOpen} />}
        {!collapsed && (
          <div className="flex items-center gap-2 px-2.5 py-1.5 bg-primary/5 rounded-lg mt-2 mx-0.5">
            <span className="sb-status-dot w-[7px] h-[7px] rounded-full bg-emerald-500 shrink-0" />
            <span className="text-[11px] font-semibold text-primary flex-1">Connected</span>
            <span className="text-[9.5px] text-muted-foreground font-mono">v2.4</span>
          </div>
        )}
        {/* Bottom spacer ensures last items scroll fully into view */}
        <div style={{ height: 8, flexShrink: 0 }} />
      </nav>

      {/* Bottom footer — minimal: preview + user + collapse only */}
      <div className="border-t border-border p-2 space-y-1 shrink-0"
        data-testid="sidebar-bottom"
      >
        {canPreview && (
          <div className="space-y-0.5">
            {!previewRole ? (
              <button
                className={`w-full flex items-center ${collapsed ? 'justify-center px-0' : 'gap-1.5 px-2'} py-1 rounded text-[10px] text-muted-foreground hover:text-foreground hover:bg-accent/60 transition-colors`}
                onClick={togglePreview} data-testid="preview-apteka-btn"
              >
                <Eye className="w-3 h-3 shrink-0" />
                {!collapsed && <span>Preview as...</span>}
              </button>
            ) : (
              <div className="flex items-center gap-1 rounded-md border border-amber-400/40 bg-amber-50 dark:bg-amber-950/40 px-1.5 py-1">
                <button
                  className="flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-400 hover:underline flex-1 min-w-0"
                  onClick={cyclePreviewRole} data-testid="cycle-preview-role-btn"
                >
                  <Eye className="w-3 h-3 shrink-0" />
                  {!collapsed && <span className="truncate">{PREVIEW_DISPLAY[previewRole] || previewRole}</span>}
                </button>
                <button
                  className="text-[9px] text-amber-600 dark:text-amber-400 hover:text-amber-800 px-1 rounded hover:bg-amber-200/50 shrink-0"
                  onClick={togglePreview}
                >
                  {collapsed ? <X className="w-3 h-3" /> : 'Exit'}
                </button>
              </div>
            )}
          </div>
        )}
        {/* User profile + Logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: collapsed ? '6px 4px' : '6px 8px', borderRadius: 8, marginBottom: 2 }}>
          <Avatar className="h-6 w-6 shrink-0"><AvatarImage src={user?.picture} /><AvatarFallback className="text-xs">{user?.name?.[0] || 'U'}</AvatarFallback></Avatar>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
              <div style={{ fontSize: 10, color: SB.mutedSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ROLE_DISPLAY[user?.role] || user?.role}</div>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Log out"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center',
                color: SB.muted, flexShrink: 0,
                transition: `color ${SB.transition}, background ${SB.transition}`,
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = SB.muted; e.currentTarget.style.background = 'transparent'; }}
            >
              <LogOut style={{ width: 15, height: 15 }} />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            onClick={handleLogout}
            data-testid="logout-btn-collapsed"
            title="Log out"
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '8px 0', background: 'transparent', border: 'none', cursor: 'pointer',
              color: SB.muted, borderRadius: 8,
              transition: `color ${SB.transition}, background ${SB.transition}`,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = SB.muted; e.currentTarget.style.background = 'transparent'; }}
          >
            <LogOut style={{ width: 15, height: 15 }} />
          </button>
        )}
        <Button
          variant="ghost" size="sm"
          className={`w-full ${collapsed ? 'px-0 justify-center' : 'justify-start gap-2'}`}
          onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-collapse-btn"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4" /><span className="text-xs">Collapse</span></>}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Command Palette */}
      <CommandPalette
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        sections={sections}
        t={t}
        hasAccess={hasAccess}
        onAction={(action) => {
          if (action === 'theme') setTheme(theme === 'dark' ? 'light' : 'dark');
          if (action === 'collapse') setCollapsed(prev => !prev);
        }}
      />
      {/* Rule 5: Overlay z-index BELOW sidebar, overlay BEFORE aside in DOM */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99] lg:hidden transition-opacity duration-300" 
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      
      {/* Mobile Sidebar — z-[100] above overlay */}
      <aside className={`fixed inset-y-0 left-0 z-[100] w-72 bg-card border-r border-border transform transition-transform duration-300 ease-out lg:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} will-change-transform shadow-2xl`}>
        {sidebarContent}
      </aside>
      
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col ${collapsed ? 'w-16' : 'w-60'} bg-card border-r border-border transition-[width] duration-300 shrink-0 will-change-[width] shadow-[1px_0_20px_rgba(0,0,0,0.04)]`}>
        {sidebarContent}
      </aside>
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="flex items-center justify-between px-4 h-12 border-b border-border bg-card/80 backdrop-blur-xl shrink-0 sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setMobileOpen(true)} 
              className="lg:hidden transition-transform active:scale-95" 
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <span className="font-semibold lg:hidden">PharmX</span>
            <div className="hidden lg:flex items-center gap-1.5 text-sm">
              <span className="text-muted-foreground text-xs font-medium">{workspaceName || 'PharmX'}</span>
              <span className="text-muted-foreground/40">/</span>
              <span className="font-semibold text-foreground tracking-tight">{activePageLabel}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCmdOpen(true)}
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border bg-muted/50 hover:bg-muted text-muted-foreground text-xs transition-colors"
              data-testid="header-cmd-trigger"
            >
              <Command className="w-3 h-3" />
              <span className="font-mono text-[10px]">&#8984;K</span>
            </button>
            <GlobalSearch />
            <NotificationBell />
          </div>
        </header>
        <PharmacyModeBar />
        {['company_owner', 'company_admin'].includes(role) && <ReturnAlertPopup />}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
